import { useCallback, useEffect, useMemo, useState } from 'react'
import { ListTodo } from 'lucide-react'
import type { Taak } from '../../gedeeld/types'
import { api } from '../lib/api'
import { isTeLaat, opDringendheid, sleutelVan, startVanDag } from '../lib/opmaak'
import { useRoute, useTitel, zetParam } from '../lib/router'
import { meldFout, openTaak, taakGewijzigd, useBasis, useStaat, useTaakWijzigingen, verwerk } from '../lib/staat'
import { Foutvlak, Kies, Laden, Leeg, Punt, Tabs } from './ui'
import { Deadline, Labels, PrioriteitIcoon, SoortIcoon, StatusPil } from './taak/stukjes'

/* ===========================================================================
 *  Mijn werk
 *
 *  Alles op jouw naam, en wat bij een rol van jou ligt en nog door niemand
 *  is opgepakt. Sinds 20 okt 2026 de enige takenlijst: Werk in het dashboard
 *  stuurt hierheen. Wat het systeem zelf klaarzet (een sollicitatie, een
 *  factuur om te tekenen) staat er ook, met waar het vandaan kwam erbij.
 *
 *  Ingedeeld op wanneer het moet, en niet op project: "wat moet ik nu doen"
 *  is een vraag over tijd.
 * =========================================================================== */

type Weergave = 'open' | 'klaar'

const bronTekst = (b: string) => (b === 'factuur' ? 'factuur om te tekenen' : b === 'sollicitatie' ? 'sollicitatie' : `uit ${b}`)

export default function MijnWerk() {
  useTitel('Mijn werk', 'Alles op jouw naam')
  const ik = useStaat((s) => s.ik)!
  const vanMij = useCallback((y: Taak) => y.toegewezenAan === ik.id
    || (!y.toegewezenAan && !!y.toegewezenRol && ik.rollen.includes(y.toegewezenRol)), [ik.id, ik.rollen])
  const { projecten } = useBasis()
  const { q } = useRoute()
  const [taken, setTaken] = useState<Taak[] | null>(null)
  const [fout, setFout] = useState<string | null>(null)
  const [weergave, setWeergave] = useState<Weergave>('open')
  const project = q.get('project') ?? ''
  const alleenLaat = q.get('filter') === 'laat'

  const laad = useCallback(() => {
    setFout(null)
    api.taken({ mijn: true }).then(setTaken).catch((e) => setFout(e instanceof Error ? e.message : String(e)))
  }, [])
  useEffect(laad, [laad])

  useTaakWijzigingen(useCallback((t) => {
    setTaken((x) => (x ? verwerk(x, t, vanMij) : x))
  }, [vanMij]))

  const zicht = useMemo(() => (taken ?? [])
    .filter((t) => (weergave === 'open' ? t.status !== 'klaar' : t.status === 'klaar'))
    .filter((t) => !project || (project === '-' ? !t.projectId : t.projectId === project))
    .filter((t) => !alleenLaat || isTeLaat(t)), [taken, weergave, project, alleenLaat])

  const groepen = useMemo(() => {
    if (weergave === 'klaar') {
      return [{ titel: 'Afgerond', taken: [...zicht].sort((a, b) => (b.klaarAt ?? 0) - (a.klaarAt ?? 0)) }]
    }
    const vandaag = startVanDag(Date.now())
    const week = vandaag + 7 * 86400000
    const indeling: { titel: string; taken: Taak[] }[] = [
      { titel: 'Te laat', taken: [] },
      { titel: 'Vandaag en morgen', taken: [] },
      { titel: 'Deze week', taken: [] },
      { titel: 'Later', taken: [] },
      { titel: 'Zonder deadline', taken: [] },
    ]
    for (const t of [...zicht].sort(opDringendheid)) {
      if (isTeLaat(t)) indeling[0]!.taken.push(t)
      else if (!t.deadline) indeling[4]!.taken.push(t)
      else if (t.deadline < vandaag + 2 * 86400000) indeling[1]!.taken.push(t)
      else if (t.deadline < week) indeling[2]!.taken.push(t)
      else indeling[3]!.taken.push(t)
    }
    return indeling.filter((g) => g.taken.length)
  }, [zicht, weergave])

  /* Een taak die bij een rol ligt op je eigen naam zetten, zoals Oppakken
     in Werk deed: daarna is hij van jou en staat hij op bezig. */
  async function oppakken(t: Taak) {
    try {
      const na = await api.taakOpslaan({ id: t.id, toegewezenAan: ik.id, toegewezenNaam: ik.naam, status: 'bezig' })
      taakGewijzigd(na)
    } catch (e) { meldFout(e) }
  }

  async function vink(t: Taak) {
    try {
      const na = await api.taakOpslaan({ id: t.id, status: t.status === 'klaar' ? 'te_doen' : 'klaar' })
      taakGewijzigd(na)
    } catch (e) { meldFout(e) }
  }

  const open = (taken ?? []).filter((t) => t.status !== 'klaar').length

  return (
    <div className="pagina">
      <header className="pagina-kop">
        <div>
          <h1>Mijn werk</h1>
          <p className="stil">Wat op jouw naam staat, en wat bij jouw rol ligt en nog door niemand is opgepakt.</p>
        </div>
      </header>

      <div className="werkbalk">
        <Tabs<Weergave> waarde={weergave} onKies={setWeergave} tabs={[
          { key: 'open', label: 'Open', teller: open },
          { key: 'klaar', label: 'Afgerond' },
        ]} />
        <span className="vul" />
        {alleenLaat && <button type="button" className="filter-pil" onClick={() => zetParam('filter', null)}>Alleen te laat ✕</button>}
        <Kies<string> klein waarde={project} onKies={(p) => zetParam('project', p || null)}
          opties={[
            { key: '', label: 'Alle projecten' },
            { key: '-', label: 'Zonder project' },
            ...projecten.filter((p) => !p.archief).map((p) => ({ key: p.id, label: p.naam, kleur: p.kleur })),
          ]} />
      </div>

      {fout ? <Foutvlak fout={fout} opnieuw={laad} /> : !taken ? <Laden /> : !groepen.length ? (
        <Leeg icoon={<ListTodo size={28} />} titel={weergave === 'open' ? 'Niets open' : 'Nog niets afgerond'}
          tekst={weergave === 'open' ? 'Er staat niets op jouw naam of bij jouw rol dat nog moet.' : undefined} />
      ) : groepen.map((g) => (
        <section key={g.titel} className="groep">
          <h2 className={`groep-kop ${g.titel === 'Te laat' ? 'gevaar' : ''}`}>{g.titel}<span className="stil">{g.taken.length}</span></h2>
          <div className="regels">
            {g.taken.map((t) => {
              const p = projecten.find((x) => x.id === t.projectId)
              return (
                <div key={t.id} className={`regel ${t.status === 'klaar' ? 'af' : ''}`}>
                  <input type="checkbox" checked={t.status === 'klaar'} onChange={() => void vink(t)} title="Afvinken" />
                  <button type="button" className="regel-open" onClick={() => openTaak(t.id)}>
                    <SoortIcoon soort={t.soort} />
                    <PrioriteitIcoon p={t.prioriteit} />
                    <span className="mono stil regel-sleutel">{sleutelVan(t, projecten) ?? ''}</span>
                    <span className="regel-titel">{t.titel}</span>
                    <Labels namen={t.labels} project={p} />
                    {p ? <span className="regel-project"><Punt kleur={p.kleur} />{p.naam}</span>
                      : t.bron !== 'handmatig' ? <span className="regel-project stil">{bronTekst(t.bron)}</span> : null}
                    <Deadline taak={t} />
                    <StatusPil status={t.status} />
                  </button>
                  {!t.toegewezenAan && t.toegewezenRol && (
                    <button type="button" className="knop k-gewoon klein" onClick={() => void oppakken(t)}
                      title={`Ligt bij de rol ${t.toegewezenRol}. Op jouw naam zetten.`}>Oppakken</button>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}
